import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CreditCard,
  Shield,
  FileText,
  BarChart2,
  Clock,
  ArrowRight,
  Database,
  Settings,
  Users,
  AlertCircle,
  CheckCircle,
  DollarSign,
  Scale,
  Activity,
  Lock
} from 'lucide-react';

// Reusable components
const FeatureCard = ({ icon: Icon, title, description }) => (
  <motion.div
    className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-indigo-500/50 transition-colors flex flex-col items-center text-center"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ scale: 1.02 }}
    transition={{ type: "spring", stiffness: 300 }}
  >
    <div className="bg-indigo-500/10 rounded-lg p-3 w-12 h-12 flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-indigo-400" />
    </div>
    <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
    <p className="text-gray-300">{description}</p>
  </motion.div>
);

const ProcessStep = ({ icon: Icon, title, description }) => (
  <motion.div
    className="flex gap-6"
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
  >
    <div className="flex-shrink-0">
      <div className="w-12 h-12 rounded-lg bg-indigo-500/10 flex items-center justify-center">
        <Icon className="w-6 h-6 text-indigo-400" />
      </div>
    </div>
    <div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-gray-300 leading-relaxed">{description}</p>
    </div>
  </motion.div>
);

const BillingAndDisputes = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-indigo-900 to-purple-900">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(99,102,241,0.15),transparent_70%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,_rgba(192,132,252,0.15),transparent_70%)]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-block mb-6">
              <div className="flex items-center gap-2 text-sm text-indigo-400 bg-indigo-500/10 rounded-full px-4 py-1.5 border border-indigo-500/20">
                <CreditCard className="w-4 h-4" />
                Billing & Disputes
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Enterprise-Grade Billing Management
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Streamlined billing operations and automated dispute resolution for high-volume transactions
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                Contact Sales
              </Link>
              <Link
                to="/documentation/billing"
                className="text-white bg-white/10 px-8 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors"
              >
                View Documentation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center justify-center mb-4">
                <Activity className="w-8 h-8 text-indigo-400" />
              </div>
              <div className="text-3xl font-bold text-white text-center mb-2">99.99%</div>
              <p className="text-gray-400 text-center">Billing Accuracy</p>
            </motion.div>
            <motion.div
              className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center justify-center mb-4">
                <Clock className="w-8 h-8 text-indigo-400" />
              </div>
              <div className="text-3xl font-bold text-white text-center mb-2">&lt;24h</div>
              <p className="text-gray-400 text-center">Average Dispute Resolution</p>
            </motion.div>
            <motion.div
              className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center justify-center mb-4">
                <Shield className="w-8 h-8 text-indigo-400" />
              </div>
              <div className="text-3xl font-bold text-white text-center mb-2">SOC 2</div>
              <p className="text-gray-400 text-center">Compliance Certified</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Enterprise Billing Features
            </h2>
            <p className="text-gray-300">
              Comprehensive tools for managing complex billing operations at scale
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard
              icon={Database}
              title="Automated Reconciliation"
              description="AI-powered matching of invoices, payments, and disputes with real-time validation."
            />
            <FeatureCard
              icon={Scale}
              title="Smart Dispute Resolution"
              description="Automated workflow for managing disputes with configurable resolution paths."
            />
            <FeatureCard
              icon={Lock}
              title="Secure Transactions"
              description="End-to-end encryption and fraud detection for all financial operations."
            />
            <FeatureCard
              icon={Users}
              title="Role-Based Access"
              description="Granular permissions and approval workflows for enterprise teams."
            />
            <FeatureCard
              icon={BarChart2}
              title="Advanced Analytics"
              description="Real-time insights into billing patterns, dispute trends, and reconciliation metrics."
            />
            <FeatureCard
              icon={Settings}
              title="Custom Integration"
              description="API-first architecture for seamless integration with existing systems."
            />
          </div>
        </div>
      </section>

      {/* Dispute Resolution Process */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Streamlined Dispute Resolution
            </h2>
            <p className="text-gray-300">
              Efficient and transparent process for handling billing disputes
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-12">
            <ProcessStep
              icon={AlertCircle}
              title="Issue Detection"
              description="Automated systems identify potential disputes through pattern recognition and anomaly detection."
            />
            <ProcessStep
              icon={FileText}
              title="Documentation Collection"
              description="Smart forms and automated document collection streamline the gathering of relevant information."
            />
            <ProcessStep
              icon={Users}
              title="Stakeholder Review"
              description="Configurable workflow routes disputes to appropriate teams with SLA tracking."
            />
            <ProcessStep
              icon={CheckCircle}
              title="Resolution & Settlement"
              description="Automated settlement processing with full audit trail and compliance documentation."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Streamline Your Billing Operations?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join leading enterprises using our platform for efficient billing management
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center"
              >
                Contact Sales
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/documentation/billing"
                className="text-white bg-white/10 px-8 py-3 rounded-lg font-medium hover:bg-white/20 transition-colors"
              >
                View Documentation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BillingAndDisputes;